import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Rating,
  IconButton,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  ArrowBack,
  FilterList,
  School,
  CheckCircle,
  Visibility,
  TrendingUp,
  Close,
} from '@mui/icons-material';
import {
  Container,
  Card,
  CardContent,
  Button,
  Loading,
  EmptyState,
  Badge,
  Alert,
} from '../design-system';
import { colors, spacing, typography } from '../design-system';
import { useTranslation } from 'react-i18next';
import { getMicroconceptos } from '../services/analisisIndividual';
import { getAllProgress, getProgressByPosition } from '../services/videoProgressHybridService';
import { useAppContext } from '../context/AppContext';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';

/**
 * AnalisisIndividualModern - Modern individual analysis page
 * Browse and filter micro-concepts for player analysis
 */
const AnalisisIndividualModern = () => {
  usePageTitle('Análisis Individual');
  const { t } = useTranslation('analysis');
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || null;
  const { tipo, setTipo } = useAppContext();
  const { posicion, setPosicion } = useAppContext();
  const { tipos, setTipos } = useAppContext();
  // eslint-disable-next-line no-unused-vars
  const { posiciones, setPosiciones } = useAppContext();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [microconceptos, setMicroconceptos] = useState([]);
  const [progress, setProgress] = useState({});
  const [watchFilter, setWatchFilter] = useState('todos');
  const [progressSummary, setProgressSummary] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);

  // Load initial data
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = (await getMicroconceptos(navigate)) || [];
        if (!mounted) return;
        
        // Normalize strings
        const normalized = data.map((c) => ({
          ...c,
          tipo: c.tipo ? String(c.tipo).trim() : '',
          posicion: c.posicion ? String(c.posicion).trim() : '',
          conceptoMicro: c.conceptoMicro ? String(c.conceptoMicro).trim() : '',
          id: c.id ?? c._rid ?? null,
        }));
        setMicroconceptos(normalized);

        // Calculate unique types and positions
        const tiposUnicos = [
          ...new Set(normalized.map((c) => c.tipo).filter(Boolean)),
        ].sort();
        const posicionesUnicas = [
          ...new Set(normalized.map((c) => c.posicion).filter(Boolean)),
        ].sort();
        setTipos(tiposUnicos);
        setPosiciones(posicionesUnicas);
      } catch (err) {
        if (mounted) setError(t('errors.loadFailed'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  // Load progress data (async hybrid service)
  useEffect(() => {
    if (!userId || microconceptos.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getAllProgress(userId, navigate);
        if (!cancelled) setProgress(data);
      } catch {
        // fallback — progress stays as-is
      }
    })();
    return () => { cancelled = true; };
  }, [userId, microconceptos, navigate]);

  // Valid positions when type changes
  const posicionesPorTipo = useMemo(() => {
    if (!tipo) {
      return [
        ...new Set(microconceptos.map((c) => c.posicion).filter(Boolean)),
      ].sort();
    }
    const pos = microconceptos
      .filter((c) => c.tipo === tipo)
      .map((c) => c.posicion)
      .filter(Boolean);
    return [...new Set(pos)].sort();
  }, [tipo, microconceptos]);

  // Reset position when type changes
  useEffect(() => {
    if (!tipo) {
      setPosiciones([
        ...new Set(microconceptos.map((c) => c.posicion).filter(Boolean)),
      ]);
      setPosicion('');
    } else {
      const nuevasPosiciones = posicionesPorTipo;
      setPosiciones(nuevasPosiciones);
      if (posicion && !nuevasPosiciones.includes(posicion)) {
        setPosicion('');
      }
    }
  }, [tipo, microconceptos, posicionesPorTipo]);

  // Filtered micro-concepts
  const conceptosFiltrados = useMemo(() => {
    let filtered = microconceptos;
    if (tipo) {
      filtered = filtered.filter((c) => c.tipo === tipo);
    }
    if (posicion) {
      filtered = filtered.filter((c) => c.posicion === posicion);
    }
    if (watchFilter === 'vistos') {
      filtered = filtered.filter((c) => c.id && progress[c.id]?.watched);
    } else if (watchFilter === 'pendientes') {
      filtered = filtered.filter((c) => !c.id || !progress[c.id]?.watched);
    }
    return filtered;
  }, [microconceptos, tipo, posicion, watchFilter, progress]);

  // Calculate progress summary (async hybrid service)
  useEffect(() => {
    if (!userId || microconceptos.length === 0) {
      setProgressSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const summary = await getProgressByPosition(userId, microconceptos, navigate);
        if (!cancelled) setProgressSummary(summary);
      } catch {
        // keep previous summary
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, microconceptos, progress, navigate]);

  const handleVerDetalle = (concepto) => {
    navigate(`/analisisIndividual/microconcepto/${concepto.id || 'detalle'}`, { state: concepto });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Loading variant="soccer" size="large" />
      </Box>
    );
  }

  return (
    <Box className="wp-page" sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {error && (
        <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={() => { setError(null); window.location.reload(); }}>{t('errors.retry')}</Button>}>
          {error}
        </Alert>
      )}
      {/* Header */}
      <Box
        className="wp-heading"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ px: 3, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/dashboard')} aria-label={t('detail.back')} sx={{ color: colors.primary[600] }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography component="h1" variant="h5" fontWeight={700} color="text.primary">
                {t('pageTitle')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Container className="wp-body" sx={{ paddingY: `${spacing.md}px` }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.sm}px` }}>
          {/* Filters — compact inline row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: `${spacing.sm}px`,
              alignItems: 'center',
              p: `${spacing.sm}px`,
              backgroundColor: 'background.paper',
              borderRadius: `${spacing.borderRadius.md}px`,
              border: `1px solid ${colors.neutral[200]}`,
            }}
          >
            <FilterList sx={{ color: colors.primary[500], fontSize: 20 }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="analysis-type-label">{t('filters.type')}</InputLabel>
              <Select
                labelId="analysis-type-label"
                value={tipo}
                label={t('filters.type')}
                onChange={(e) => setTipo(e.target.value)}
              >
                <MenuItem value="">{t('filters.all')}</MenuItem>
                {tipos.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="analysis-position-label">{t('filters.position')}</InputLabel>
              <Select
                labelId="analysis-position-label"
                value={posicion}
                label={t('filters.position')}
                onChange={(e) => setPosicion(e.target.value)}
                disabled={!tipo}
              >
                <MenuItem value="">{t('filters.all')}</MenuItem>
                {posicionesPorTipo.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {userId && (
              <Box sx={{ display: 'flex', gap: `${spacing.xs}px`, flexWrap: 'wrap' }}>
                <Chip
                  label={t('filters.all')}
                  size="small"
                  onClick={() => setWatchFilter('todos')}
                  variant={watchFilter === 'todos' ? 'filled' : 'outlined'}
                  color={watchFilter === 'todos' ? 'primary' : 'default'}
                />
                <Chip
                  label={t('filters.watched')}
                  size="small"
                  onClick={() => setWatchFilter('vistos')}
                  variant={watchFilter === 'vistos' ? 'filled' : 'outlined'}
                  color={watchFilter === 'vistos' ? 'primary' : 'default'}
                  icon={<CheckCircle sx={{ fontSize: 16 }} />}
                />
                <Chip
                  label={t('filters.pending')}
                  size="small"
                  onClick={() => setWatchFilter('pendientes')}
                  variant={watchFilter === 'pendientes' ? 'filled' : 'outlined'}
                  color={watchFilter === 'pendientes' ? 'primary' : 'default'}
                  icon={<Visibility sx={{ fontSize: 16 }} />}
                />
              </Box>
            )}
          </Box>

          {/* Results count */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: `${spacing.xs}px`,
              padding: `${spacing.sm}px ${spacing.md}px`,
              backgroundColor: 'action.hover',
              borderRadius: `${spacing.borderRadius.md}px`,
              borderLeft: `4px solid ${colors.primary[500]}`,
            }}
          >
            <School sx={{ color: colors.primary[600], fontSize: 20 }} />
            <Typography variant="body1" fontWeight={typography.fontWeight.semibold} sx={{ flex: 1 }}>
              {t('results.count', { count: conceptosFiltrados.length })}
            </Typography>
            {progressSummary && (
              <Chip
                icon={<TrendingUp sx={{ fontSize: 16 }} />}
                label={`${progressSummary.watched}/${progressSummary.total} vistos — Ver progreso ›`}
                size="small"
                onClick={() => setProgressModalOpen(true)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: colors.primary[50],
                  color: colors.primary[700],
                  border: `1.5px solid ${colors.primary[300]}`,
                  fontWeight: typography.fontWeight.semibold,
                  px: 1,
                  transition: 'all 0.2s ease',
                  animation: 'subtlePulse 2.5s ease-in-out 3',
                  '@keyframes subtlePulse': {
                    '0%, 100%': { boxShadow: 'none' },
                    '50%': { boxShadow: `0 0 0 4px ${colors.primary[100]}` },
                  },
                  '&:hover': {
                    backgroundColor: colors.primary[100],
                    borderColor: colors.primary[500],
                    transform: 'scale(1.04)',
                  },
                }}
              />
            )}
          </Box>

          {/* Micro-concepts Grid (Videos) */}
          {conceptosFiltrados.length > 0 ? (
            <Grid container spacing={`${`${spacing.sm}px`}px`} key={`grid-${tipo}-${posicion}-${watchFilter}`}>
              {conceptosFiltrados.map((concepto, index) => (
                <Grid item xs={12} sm={6} lg={4} key={concepto.id || `${concepto.conceptoMicro}-${index}`}>
                  <Card
                    component="button"
                    type="button"
                    aria-label={concepto.conceptoMicro}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      font: 'inherit',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 8px 24px ${colors.neutral[200]}`,
                      },
                    }}
                    onClick={() => handleVerDetalle(concepto)}
                  >
                    {userId && concepto.id && progress[concepto.id]?.watched && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 22, color: colors.success[500] }} />
                      </Box>
                    )}
                    <CardContent sx={{ flex: 1, p: `${spacing.sm}px` }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={typography.fontWeight.bold}
                        sx={{ mb: `${spacing.xs}px`, pr: 3 }}
                      >
                        {concepto.conceptoMicro}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: `${spacing.xs}px`, flexWrap: 'wrap', mb: `${spacing.sm}px` }}>
                        {concepto.tipo && (
                          <Badge variant="outlined" color="default">{concepto.tipo}</Badge>
                        )}
                        {concepto.posicion && (
                          <Badge variant="outlined" color="default">{concepto.posicion}</Badge>
                        )}
                        {userId && concepto.id && progress[concepto.id]?.watched && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `2px ${spacing.xs}px`,
                              backgroundColor: colors.success[50],
                              color: colors.success[700],
                              border: `1px solid ${colors.success[200]}`,
                              borderRadius: `${spacing.borderRadius.full}px`,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.semibold,
                            }}
                          >
                            {t('card.watchedCount', { count: progress[concepto.id].viewCount })}
                          </Box>
                        )}
                        {userId && concepto.id && progress[concepto.id]?.secondsWatched > 0 && !progress[concepto.id]?.watched && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `2px ${spacing.xs}px`,
                              backgroundColor: colors.warning[50],
                              color: colors.warning[700],
                              border: `1px solid ${colors.warning[200]}`,
                              borderRadius: `${spacing.borderRadius.full}px`,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.semibold,
                            }}
                          >
                            {t('card.inProgress')}
                          </Box>
                        )}
                      </Box>

                      {concepto.Dificultad && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${spacing.xs}px`, mb: `${spacing.xs}px` }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {t('card.difficulty')}
                          </Typography>
                          <Rating
                            name={`dificultad-${index}`}
                            value={concepto.Dificultad}
                            max={3}
                            readOnly
                            size="small"
                          />
                        </Box>
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {concepto.descripcion || t('card.noDescription')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <EmptyState
              type="no-data"
              title={t('empty.title')}
              message={t('empty.message')}
            />
          )}

        </Box>
      </Container>

      {/* Progress Modal */}
      <Dialog
        open={progressModalOpen}
        onClose={() => setProgressModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ color: colors.success[600] }} />
            <Typography variant="h6" fontWeight={typography.fontWeight.bold}>Progreso</Typography>
          </Box>
          <IconButton onClick={() => setProgressModalOpen(false)} size="small" aria-label={t('progress.close')}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {progressSummary && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.md}px` }}>
              {/* Overall bar */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: `${spacing.xs}px` }}>
                  <Typography variant="body2" fontWeight={typography.fontWeight.semibold}>
                    {t('progress.totalProgress')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progressSummary.watched} de {progressSummary.total} — {Math.round((progressSummary.watched / progressSummary.total) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(progressSummary.watched / progressSummary.total) * 100}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': { backgroundColor: colors.success[500], borderRadius: 5 },
                  }}
                />
              </Box>

              {/* Per-position breakdown */}
              {Object.entries(progressSummary.byPosition).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: `${spacing.sm}px` }}>
                    Por posición
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.sm}px` }}>
                    {Object.entries(progressSummary.byPosition).map(([pos, data]) => (
                      <Box key={pos}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                          <Typography variant="body2" fontWeight={typography.fontWeight.semibold}>{pos}</Typography>
                          <Typography variant="body2" color="text.secondary">{data.watched}/{data.total}</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(data.watched / data.total) * 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'action.hover',
                            '& .MuiLinearProgress-bar': { backgroundColor: colors.primary[500], borderRadius: 3 },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AnalisisIndividualModern;
